import * as React from "react";
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from "react";
import debounce from "lodash/debounce";
import {
    BoxButtonWrapper,
    BoxModalContent,
    BoxSearchSubText,
    BoxSearchWrapper,
    ButtonCancel,
    CommunityCircularProgressWrapper,
    GroupSearchNoResultsSubtitle,
    ListContainer,
    ListSearchResultsWrapper,
    ListSubHeaderWrapper,
    StackSearchContainer,
} from "./SearchContent.styles";
import { useContentSearchStore } from "../useContentSearchStore";
import { ContentResultList } from "./ContentResultList/ContentResultList";
import {
    Content,
    ContentType,
} from "./ContentResultList/ContentListItem/ContentListItem.types";
import {
    SearchContentData,
    SearchContentProps,
    SearchContentRequestStatusEnum,
} from "./SearchContent.types";
import { CommunityCircularProgress } from "@shared/styles";
import {
    emptyQuerySearchResults,
    searchByContent,
} from "../ContentSearchService";
import OverlaySearchTextField from "../OverlaySearchTextField";
import { usePostDetailsNavigationActions } from "@infrastructure/Hooks/usePostDetailsNavigationActions";
import { PostDetailsPageActions } from "../../PostDetailsModal/PostDetailsModal";

const SEARCH_TERM_MIN_LIMIT = 3;
export const DEFAULT_QUERY_SEARCH_PARAM = "";
const DEFAULT_SUGGESTIONS_ITEMS_TO_DISPLAY = 10;
export const DEFAULT_DEBOUNCE_MILLISECONDS = 700;

export function SearchContent({
    closeSearchModal,
    groupIds,
}: SearchContentProps) {
    const {
        set: setSearchedTermsAndResult,
        reset: resetSearchedTermsAndResult,
        requestStatus: previousRequestStatus,
        searchValue: previousSearchValue,
        contentList: previousContentList,
        groupIds: previousGroupIds,
        scrollPosition,
        setScrollPosition,
    } = useContentSearchStore();
    const { navigateToPostWithAction } = usePostDetailsNavigationActions();
    const navigateToPost = useCallback(
        (
            id: number,
            desiredAction?: {
                action: PostDetailsPageActions;
                commentId: string;
            },
        ) => {
            navigateToPostWithAction(
                id,
                desiredAction?.action,
                desiredAction?.commentId,
            );
            // when switching modals we need them to "overlap", otherwise `useBodyScrollModalControlState` won't work correctly
            setTimeout(closeSearchModal, 100);
        },
        [closeSearchModal, navigateToPostWithAction],
    );

    const [searchContentData, setSearchContentData] =
        useState<SearchContentData>({
            contentList:
                previousGroupIds === groupIds ? previousContentList : undefined,
            defaultList: undefined,
            groupIds,
            searchValue: previousSearchValue,
            requestStatus: previousRequestStatus,
        });

    const onSearchInputChange = useCallback(
        (searchValue: string) => {
            const hasDefaultList = searchContentData.defaultList?.length;
            const hasContent = !!searchContentData.contentList?.length;

            if (searchValue.length < SEARCH_TERM_MIN_LIMIT) {
                if (searchValue.length === 0) {
                    if (hasDefaultList) {
                        if (hasContent) {
                            setSearchContentData((prevState) => ({
                                ...prevState,
                                searchValue,
                                contentList: undefined,
                                requestStatus:
                                    SearchContentRequestStatusEnum.DEFAULT_DATA_LOADED,
                            }));
                        }
                        return;
                    }
                } else if (hasContent) {
                    setSearchContentData((prevState) => ({
                        ...prevState,
                        searchValue,
                        requestStatus:
                            SearchContentRequestStatusEnum.RESULTS_LOADED,
                    }));
                    return;
                } else if (hasDefaultList) {
                    setSearchContentData((prevState) => ({
                        ...prevState,
                        searchValue,
                        contentList: undefined,
                        requestStatus:
                            SearchContentRequestStatusEnum.DEFAULT_DATA_LOADED,
                    }));
                    return;
                }
            }
            doSearch(
                groupIds,
                searchContentData,
                setSearchContentData,
                searchValue,
            );
        },
        [groupIds],
    );

    const handleScroll = useCallback(
        (node: HTMLDivElement) => {
            if (
                node &&
                searchContentData.requestStatus ===
                    SearchContentRequestStatusEnum.RESULTS_LOADED &&
                searchContentData.contentList?.length
            ) {
                scrollPosition &&
                    node.scrollTo({
                        top: scrollPosition,
                        behavior: "smooth",
                    });

                node.onscroll = () => {
                    setScrollPosition(node.scrollTop ?? 0);
                };
            }
        },
        [
            scrollPosition,
            searchContentData.contentList?.length,
            searchContentData.requestStatus,
            setScrollPosition,
        ],
    );

    useEffect(() => {
        if (groupIds !== searchContentData.groupIds) {
            setSearchContentData({
                contentList: undefined,
                defaultList: undefined,
                groupIds,
                searchValue: "",
                requestStatus: SearchContentRequestStatusEnum.LOADING,
            });
            onSearchInputChange("");
        }
    }, [groupIds, onSearchInputChange, searchContentData.groupIds]);

    const navigateToContent = useCallback(
        (content: Content) => {
            switch (content.contentType) {
                case ContentType.POST:
                    navigateToPost(content.id);
                    break;
                case ContentType.COMMENT:
                    navigateToPost(content.parentId, {
                        action: PostDetailsPageActions.PIN_COMMENT,
                        commentId: content.id.toString(),
                    });
                    break;
            }
        },
        [navigateToPost],
    );

    const onContentItemClick = useCallback(
        (content: Content) => {
            setSearchedTermsAndResult({
                ...searchContentData,
            });
            navigateToContent(content);
        },
        [setSearchedTermsAndResult, searchContentData, navigateToContent],
    );

    const onSearchModalDismissed = () => {
        resetSearchedTermsAndResult();
        closeSearchModal();
    };

    return (
        <BoxModalContent>
            <ListSearchResultsWrapper
                subheader={
                    <ListSubHeaderWrapper component="div">
                        <StackSearchContainer direction="row">
                            <BoxSearchWrapper>
                                <OverlaySearchTextField
                                    defaultValue={previousSearchValue}
                                    onSearch={onSearchInputChange}
                                    placeholder="Community durchsuchen"
                                />
                            </BoxSearchWrapper>
                            <BoxButtonWrapper onClick={onSearchModalDismissed}>
                                <ButtonCancel>Abbrechen</ButtonCancel>
                            </BoxButtonWrapper>
                        </StackSearchContainer>
                    </ListSubHeaderWrapper>
                }
            >
                <ListContainer ref={handleScroll}>
                    {searchContentData.requestStatus ===
                        SearchContentRequestStatusEnum.LOADING && (
                        <CommunityCircularProgressWrapper>
                            <CommunityCircularProgress />
                        </CommunityCircularProgressWrapper>
                    )}
                    {searchContentData.requestStatus ===
                        SearchContentRequestStatusEnum.RESULTS_LOADED && (
                        <>
                            {searchContentData.contentList !== undefined && (
                                <>
                                    {!!searchContentData.contentList
                                        ?.length && (
                                        <>
                                            <BoxSearchSubText>
                                                Vorschläge
                                            </BoxSearchSubText>
                                            <ContentResultList
                                                items={
                                                    searchContentData.contentList
                                                }
                                                onItemClick={onContentItemClick}
                                                searchTerms={
                                                    searchContentData.searchValue
                                                }
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {searchContentData.requestStatus ===
                        SearchContentRequestStatusEnum.DEFAULT_DATA_LOADED && (
                        <>
                            <BoxSearchSubText>
                                Beliebte Beiträge
                            </BoxSearchSubText>
                            {!!searchContentData.defaultList?.length && (
                                <ContentResultList
                                    items={searchContentData.defaultList.slice(
                                        0,
                                        DEFAULT_SUGGESTIONS_ITEMS_TO_DISPLAY,
                                    )}
                                    onItemClick={onContentItemClick}
                                />
                            )}
                        </>
                    )}
                    {searchContentData.requestStatus ===
                        SearchContentRequestStatusEnum.EMPTY_RESULTS_LOADED && (
                        <>
                            {searchContentData.searchValue ? (
                                <GroupSearchNoResultsSubtitle>
                                    Wir haben alles durchsucht, leider ohne
                                    Erfolg. Versuche es mit einem anderen
                                    Begriff.
                                </GroupSearchNoResultsSubtitle>
                            ) : (
                                <GroupSearchNoResultsSubtitle>
                                    Gib einen Suchbegriff ein, um passende
                                    Beiträge, Gruppen oder Reiseziele zu finden.
                                </GroupSearchNoResultsSubtitle>
                            )}
                            {!!searchContentData.defaultList?.length && (
                                <ContentResultList
                                    items={searchContentData.defaultList.slice(
                                        0,
                                        DEFAULT_SUGGESTIONS_ITEMS_TO_DISPLAY,
                                    )}
                                    onItemClick={onContentItemClick}
                                />
                            )}
                        </>
                    )}
                </ListContainer>
            </ListSearchResultsWrapper>
        </BoxModalContent>
    );
}

const doSearch = debounce(
    (
        groupIds: number[],
        searchContentData: SearchContentData,
        setSearchContentData: Dispatch<SetStateAction<SearchContentData>>,
        searchValue: string,
    ) => {
        if (
            searchContentData.searchValue === searchValue &&
            searchContentData.contentList?.length
        ) {
            return;
        }

        setSearchContentData((prevState) => ({
            contentList: undefined,
            defaultList:
                groupIds !== prevState.groupIds
                    ? undefined
                    : prevState.defaultList,
            groupIds,
            searchValue,
            requestStatus: SearchContentRequestStatusEnum.LOADING,
        }));

        const setErrorState = () => {
            setSearchContentData((prevState) => ({
                ...prevState,
                contentList: undefined,
                requestStatus: SearchContentRequestStatusEnum.ERROR,
            }));
        };

        if (!searchValue) {
            emptyQuerySearchResults(groupIds)
                .then((defaultList) => {
                    setSearchContentData((prevState) => ({
                        ...prevState,
                        contentList: undefined,
                        defaultList,
                        requestStatus: defaultList.length
                            ? SearchContentRequestStatusEnum.DEFAULT_DATA_LOADED
                            : SearchContentRequestStatusEnum.EMPTY_RESULTS_LOADED,
                    }));
                })
                .catch(setErrorState);
        } else {
            searchByContent(searchValue, groupIds)
                .then((contentList) => {
                    setSearchContentData((prevState) => ({
                        ...prevState,
                        contentList,
                        requestStatus: contentList.length
                            ? SearchContentRequestStatusEnum.RESULTS_LOADED
                            : SearchContentRequestStatusEnum.EMPTY_RESULTS_LOADED,
                    }));
                })
                .catch(setErrorState);
        }
    },
    DEFAULT_DEBOUNCE_MILLISECONDS,
);
